import "./app.css";
import AOS from "aos";
import "aos/dist/aos.css";
import RoutesWrapper from "./routes/routes-wrapper";
import { Libraries, LoadScript } from "@react-google-maps/api";
import Toast from "./components/toast";
import Navbar from "./components/redesign/Navbar";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const libraries: Libraries = ["places"];

const App: React.FC = () => {
  const location = useLocation();
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY!;

  useEffect(() => {
    AOS.init({
      offset: 120,
      delay: 0,
      duration: 1200,
      easing: "ease",
      once: true,
      mirror: false,
    });
  });

  useEffect(() => {
    // Paths that land on the marketing page but should auto-scroll to a
    // specific section instead of resetting to the top — e.g. a direct link
    // to the roles tab, keyed by pathname rather than a URL hash.
    const SECTION_BY_PATH: Record<string, string> = {
      "/for-buyers": "roles",
      "/for-vendors": "roles",
    };
    const id = location.hash
      ? location.hash.substring(1)
      : SECTION_BY_PATH[location.pathname];
    const element = id ? document.getElementById(id) : null;
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  // Auto-reload when returning to the tab after being away for 30+ minutes
  useEffect(() => {
    const THRESH = process.env.REACT_APP_AUTO_RELOAD_THRESHOLD
      ? parseInt(process.env.REACT_APP_AUTO_RELOAD_THRESHOLD)
      : 30 * 60 * 1000; // 30 minutes
    let hiddenAt = document.hidden ? Date.now() : 0;
    let reloading = false;

    const onHide = () => {
      hiddenAt = Date.now();
    };

    const onResume = () => {
      if (!reloading && hiddenAt && Date.now() - hiddenAt >= THRESH) {
        reloading = true;
        window.location.reload();
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) onHide();
      else onResume();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onResume);
    window.addEventListener("pageshow", onResume);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onResume);
      window.removeEventListener("pageshow", onResume);
    };
  }, []);

  const navRootes = [
    // Marketing routes ("/", "/about", "/contact", "/faq", "/terms",
    // "/privacy-policy") are intentionally omitted — they render inside
    // MarketingLayout, which mounts the new navbar/footer once
    // (components/landing2/*). Listing them here would show two navbars.
    "/products",
    "/mediation",
  ];

  const showOffset = useMemo((): boolean => {
    let show = false;
    for (let i = 0; i < navRootes.length; i++) {
      if (location.pathname === navRootes[i]) {
        show = true;
        break;
      }
    }
    return show;
  }, [location.pathname]);

  // Only load the Google Maps script on routes that actually use it
  // (create-order's delivery address autocomplete). Loading it globally made
  // every page — including the landing — fetch the heavy Maps script.
  const needsMaps = location.pathname.startsWith("/create-order");

  const content = (
    <main>
      {navRootes.includes(location.pathname) && <Navbar />}
      {showOffset && <div className='h-[80px] w-full'></div>}
      <RoutesWrapper />
      <Toast />
    </main>
  );

  if (needsMaps) {
    return (
      <LoadScript googleMapsApiKey={apiKey} libraries={libraries}>
        {content}
      </LoadScript>
    );
  }

  return content;
};

export default App;
